import * as React from 'react';
import { CSSProperties } from 'react';
import { bookingType } from 'Models/Enums';
import { themeStore } from 'Models/ThemeStore';
import { Flex } from 'Views/Components/Flex/Flex';
import If from 'Views/Components/If/If';
import { isNotNullOrUndefined, stringNotEmpty } from 'Util/TypeGuards';
import { getAddOnsToShow, isAnyAddOnsToShow } from '../CheckInUtils';
import { CheckInBookingOverviewDto } from '../CheckInEntities/CheckInBookingOverviewDto';
import { Tag } from './Tag';
import useCheckInStore from 'Hooks/useCheckInStore';
import { CargoCharacteristicDto, mapCargoCharacteristicEntityToDto } from 'Modules/Cargo';
import CargoCharacteristicTags from 'Views/Components/_HumanWritten/Tag/CargoCharacteristicTags';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { getCheckInDisplayEntries, useDynamicBookingFieldsStore } from 'Stores/DynamicBookingFieldsStore';

export interface CheckInListRowTagsProps {
	booking: CheckInBookingOverviewDto;
	typeOfBooking: bookingType;
}

/**
 * Creates a transparent tag style: semi-transparent background with a
 * darkened version of the colour for the text to ensure readability.
 */
export function transparentTagStyle(colour: string | null | undefined): CSSProperties {
	const resolvedColour = colour || 'var(--colour-brand-primary)';
	return {
		backgroundColor: `color-mix(in srgb, ${resolvedColour} 25%, transparent)`,
		color: `color-mix(in srgb, ${resolvedColour} 50%, black)`,
	};
}

export function CheckInListRowTags({ booking, typeOfBooking }: CheckInListRowTagsProps) {
	const checkInStore = useCheckInStore();
	// Subscribe to the field cache so the row re-renders once it loads — reading via
	// getState() would leave the tags blank until some other update forces a re-render.
	const dynamicFields = useDynamicBookingFieldsStore(s => s.fields);

	const cargoCharacteristicMap = React.useMemo(() => {
		const map: Record<string, CargoCharacteristicDto> = {};
		for (const characteristic of checkInStore.cargoCharacteristics) {
			map[characteristic.id] = mapCargoCharacteristicEntityToDto(characteristic);
		}
		return map;
	}, [checkInStore.cargoCharacteristics]);

	const hasUnpaid = booking?.transactionStatus === 'UNPAID';
	const hasUserDisplayName = isNotNullOrUndefined(booking?.user?.userDisplayName);
	const hasAddOns = isAnyAddOnsToShow(booking);
	// Combine the vehicle and trailer characteristics into one list. CargoCharacteristicTags
	// de-dupes by id (so a characteristic shared by the vehicle and trailer only shows one tag) and,
	// with respectVisibility, limits the tags to those flagged "show to staff".
	const combinedCharacteristics =
		typeOfBooking === 'VEHICLE'
			? [
					...(booking.bookedSummary?.effectiveCharacteristics ?? []),
					...(booking.bookedSummary?.trailerEffectiveCharacteristics ?? []),
				]
			: [];
	const hasCharacteristics = combinedCharacteristics.length > 0;
	const hasFreightHandling =
		typeOfBooking === 'FREIGHT' && booking.getFreightHandlingList().filter(h => h.showInCheckIn).length > 0;

	// Dynamic fields configured with a TAG / ICON check-in display, carrying a value.
	const dynamicEntries = getCheckInDisplayEntries(dynamicFields, booking);
	const tagEntries = dynamicEntries.filter(e => e.field.checkInDisplay === 'TAG');
	const iconEntries = dynamicEntries.filter(e => e.field.checkInDisplay === 'ICON' && stringNotEmpty(e.field.icon));

	if (
		!hasUnpaid &&
		!hasUserDisplayName &&
		!hasAddOns &&
		!hasCharacteristics &&
		!hasFreightHandling &&
		tagEntries.length === 0 &&
		iconEntries.length === 0
	) {
		return null;
	}

	return (
		<Flex direction="row" gap="xxxs" flexWrap="wrap" className="check-in-list__row-tags">
			<If condition={hasUnpaid}>
				<Tag
					text={booking?.transactionStatus ?? 'UNPAID'}
					customStyles={transparentTagStyle(themeStore.awaitingTagColour)}
					tagClassName="custom"
				/>
			</If>
			<If condition={hasUserDisplayName}>
				<Tag
					text={booking?.user?.userDisplayName?.displayName}
					customStyles={transparentTagStyle(booking?.user?.userDisplayName?.tagColour)}
				/>
			</If>
			<If condition={hasAddOns}>
				{getAddOnsToShow(booking)?.map(addOn => (
					<Tag
						key={addOn.option.id}
						text={stringNotEmpty(addOn.option.abbreviation) ? addOn.option.abbreviation : addOn.option.name}
						customStyles={transparentTagStyle(addOn?.option?.colour)}
					/>
				))}
			</If>
			<If condition={hasCharacteristics}>
				<CargoCharacteristicTags
					effectiveCharacteristics={combinedCharacteristics}
					cargoCharacteristicMap={cargoCharacteristicMap}
					respectVisibility
				/>
			</If>
			<If condition={hasFreightHandling}>
				{booking
					.getFreightHandlingList()
					.filter(h => h.showInCheckIn)
					.map(h => {
						const colour = h?.colour ?? '#000000';
						return (
							<Tag
								key={h.label}
								text={stringNotEmpty(h.abbreviation) ? h.abbreviation : h.label}
								customStyles={{
									...transparentTagStyle(colour),
									border: `1px solid ${colour}`,
								}}
							/>
						);
					})}
			</If>
			<If condition={tagEntries.length > 0}>
				{tagEntries.map(({ field }) => (
					<Tag
						key={field.id}
						text={stringNotEmpty(field.abbreviation) ? field.abbreviation : field.label}
						customStyles={transparentTagStyle(field.colour)}
						tagClassName="custom"
					/>
				))}
			</If>
			<If condition={iconEntries.length > 0}>
				{iconEntries.map(({ field }) => (
					<span
						key={field.id}
						className="check-in-list__row-tag-icon"
						style={field.colour ? { color: field.colour } : undefined}
						title={field.label}
					>
						<Icon name={field.icon as string} useMaterialIcons />
					</span>
				))}
			</If>
		</Flex>
	);
}
